import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import theme from "../../../theme";
import {
  createAdminProduct,
  deleteAdminProduct,
  deleteAdminProductImage,
  getAdminProducts,
  updateAdminProduct,
  uploadAdminProductImage,
} from "../../../services/core-api";
import {
  AdminProduct,
  AdminProductPayload,
} from "../../../interfaces/core/AdminProduct";
import { useSnackbar } from "../../../components/common/SnackbarContext";

const emptyForm: AdminProductPayload = {
  articleNumber: "",
  internalNumber: "",
  dataSupplierId: 0,
  mfrName: "",
  erp_id: null,
  description: "",
  related_number: "",
  more_related_numbers: "",
  category: "Spare Part",
  open_status: false,
};

const CATEGORY_OPTIONS = ["Spare Part", "GOMA", "BATERI", "oil"];

const ProductsPage = () => {
  const baseURL = process.env.REACT_APP_API_URL || "";
  const { showSnackbar } = useSnackbar();
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(20);
  const [count, setCount] = React.useState(0);
  const [items, setItems] = React.useState<AdminProduct[]>([]);
  const [searchValue, setSearchValue] = React.useState("");
  const [categoryFilter, setCategoryFilter] = React.useState("");
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<AdminProduct | null>(null);
  const [form, setForm] = React.useState<AdminProductPayload>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadTargetId, setUploadTargetId] = React.useState<number | null>(
    null
  );

  const resolveImageSrc = (row: AdminProduct) => {
    if (row.image_url) {
      if (
        row.image_url.startsWith("http://") ||
        row.image_url.startsWith("https://")
      ) {
        return row.image_url;
      }
      return `${baseURL}${row.image_url}`;
    }
    if (row.image) {
      if (row.image.startsWith("http://") || row.image.startsWith("https://")) {
        return row.image;
      }
      return `${baseURL}/${row.image}`;
    }
    return "";
  };

  const fetchProducts = React.useCallback(async () => {
    try {
      const response = await getAdminProducts({
        page,
        limit,
        q: searchValue || undefined,
        category: categoryFilter || undefined,
      });
      setItems(response.items || []);
      setCount(Math.max(1, Math.ceil((response.total || 0) / limit)));
    } catch {
      setItems([]);
      setCount(0);
      showSnackbar("Failed to load products", "error");
    }
  }, [page, limit, searchValue, categoryFilter, showSnackbar]);

  React.useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (product: AdminProduct) => {
    setEditing(product);
    setForm({
      articleNumber: product.articleNumber || "",
      internalNumber: product.internalNumber || "",
      dataSupplierId: product.dataSupplierId ?? 0,
      mfrName: product.mfrName || "",
      erp_id: product.erp_id ?? null,
      description: product.description || "",
      related_number: product.related_number || "",
      more_related_numbers: product.more_related_numbers || "",
      category: product.category || "",
      open_status: Boolean(product.open_status),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.mfrName?.trim()) {
      showSnackbar("Manufacturer name is required", "error");
      return;
    }
    setSaving(true);
    try {
      const payload: AdminProductPayload = {
        ...form,
        dataSupplierId: Number(form.dataSupplierId) || 0,
        erp_id:
          form.erp_id === null || form.erp_id === ("" as any)
            ? null
            : Number(form.erp_id),
      };
      if (editing) {
        await updateAdminProduct(editing.id, payload);
        showSnackbar("Product updated", "success");
      } else {
        await createAdminProduct(payload);
        showSnackbar("Product created", "success");
      }
      setDialogOpen(false);
      fetchProducts();
    } catch {
      showSnackbar("Failed to save product", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await deleteAdminProduct(id);
      showSnackbar("Product deleted", "success");
      fetchProducts();
    } catch {
      showSnackbar("Failed to delete product", "error");
    }
  };

  const handleUploadClick = (id: number) => {
    setUploadTargetId(id);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    const id = uploadTargetId;
    event.target.value = "";
    if (!file || !id) return;
    try {
      await uploadAdminProductImage(id, file);
      showSnackbar("Image uploaded", "success");
      fetchProducts();
    } catch {
      showSnackbar("Failed to upload image", "error");
    }
  };

  const handleClearImage = async (id: number) => {
    try {
      await deleteAdminProductImage(id);
      showSnackbar("Image removed", "success");
      fetchProducts();
    } catch {
      showSnackbar("Failed to remove image", "error");
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" sx={{ fontFamily: "Inter", fontWeight: 600 }}>
          Products
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{
            backgroundColor: theme.palette.secondary.main,
            textTransform: "none",
          }}
        >
          Add product
        </Button>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder="Search article / internal / brand"
          value={searchValue}
          onChange={(e) => {
            setPage(1);
            setSearchValue(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280, backgroundColor: "#fff" }}
        />
        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => {
            setPage(1);
            setCategoryFilter(e.target.value);
          }}
          sx={{ minWidth: 180, backgroundColor: "#fff" }}
        >
          <MenuItem value="">All</MenuItem>
          {CATEGORY_OPTIONS.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileChange}
      />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Article</TableCell>
              <TableCell>Internal</TableCell>
              <TableCell>Brand</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>ERP</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((row) => {
              const src = resolveImageSrc(row);
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    {src ? (
                      <img
                        src={src}
                        alt=""
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: "contain",
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No image
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{row.articleNumber || "—"}</TableCell>
                  <TableCell>{row.internalNumber || "—"}</TableCell>
                  <TableCell>{row.mfrName}</TableCell>
                  <TableCell>{row.dataSupplierId}</TableCell>
                  <TableCell>{row.category || "—"}</TableCell>
                  <TableCell>{row.erp_id ?? "—"}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      title="Upload image"
                      onClick={() => handleUploadClick(row.id)}
                    >
                      <CloudUploadIcon fontSize="small" />
                    </IconButton>
                    {row.image && (
                      <IconButton
                        size="small"
                        title="Remove image"
                        onClick={() => handleClearImage(row.id)}
                      >
                        <DeleteIcon fontSize="small" color="warning" />
                      </IconButton>
                    )}
                    <IconButton size="small" onClick={() => openEdit(row)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(row.id)}
                    >
                      <DeleteIcon fontSize="small" color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No products found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
        <Pagination
          count={count}
          page={page}
          onChange={(_, p) => setPage(p)}
          color="primary"
        />
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing ? `Edit product #${editing.id}` : "Create product"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Article number"
              value={form.articleNumber || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, articleNumber: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Internal number"
              value={form.internalNumber || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, internalNumber: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Manufacturer"
              value={form.mfrName}
              onChange={(e) =>
                setForm((f) => ({ ...f, mfrName: e.target.value }))
              }
              required
              fullWidth
            />
            <TextField
              label="Data supplier ID"
              type="number"
              value={form.dataSupplierId}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  dataSupplierId: Number(e.target.value) || 0,
                }))
              }
              fullWidth
            />
            <TextField
              select
              label="Category"
              value={form.category || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, category: e.target.value }))
              }
              fullWidth
            >
              {CATEGORY_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="ERP ID"
              type="number"
              value={form.erp_id ?? ""}
              onChange={(e) =>
                setForm((f) => ({
                  ...f,
                  erp_id: e.target.value === "" ? null : Number(e.target.value),
                }))
              }
              fullWidth
            />
            <TextField
              label="Related number"
              value={form.related_number || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, related_number: e.target.value }))
              }
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description || ""}
              onChange={(e) =>
                setForm((f) => ({ ...f, description: e.target.value }))
              }
              fullWidth
              multiline
              minRows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(form.open_status)}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, open_status: e.target.checked }))
                  }
                />
              }
              label="Open status"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ backgroundColor: theme.palette.secondary.main }}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsPage;
